import React, { useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X } from 'lucide-react';
import { toast } from 'sonner';

interface AthleteProfileEditDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: 'overview' | 'athletic' | 'personal' | 'contact';
  data: any;
  onSave: (updatedData: any) => void;
}

const AthleteProfileEditDrawer: React.FC<AthleteProfileEditDrawerProps> = ({
  open,
  onOpenChange,
  section,
  data,
  onSave
}) => {
  const [formData, setFormData] = useState(data);
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await onSave(formData);
      toast.success('Profile updated successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const renderOverviewFields = () => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">First Name</Label>
          <Input
            id="first_name"
            value={formData.athlete?.first_name || ''}
            onChange={(e) => {
              const newFirstName = e.target.value;
              const lastName = formData.athlete?.last_name || '';
              const fullName = `${newFirstName} ${lastName}`.trim();
              setFormData({
                ...formData,
                athlete: { 
                  ...formData.athlete, 
                  first_name: newFirstName,
                  name: fullName
                },
                display_name: fullName
              });
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">Last Name</Label>
          <Input
            id="last_name"
            value={formData.athlete?.last_name || ''}
            onChange={(e) => {
              const newLastName = e.target.value;
              const firstName = formData.athlete?.first_name || '';
              const fullName = `${firstName} ${newLastName}`.trim();
              setFormData({
                ...formData,
                athlete: { 
                  ...formData.athlete, 
                  last_name: newLastName,
                  name: fullName
                },
                display_name: fullName
              });
            }}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="position">Position</Label>
        <Input
          id="position"
          value={formData.athlete?.position || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, position: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="date_of_birth">Date of Birth</Label>
        <Input
          id="date_of_birth"
          type="date"
          value={formData.athlete?.date_of_birth || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, date_of_birth: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="bio">Bio</Label>
        <Textarea
          id="bio"
          value={formData.bio || ''}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
          rows={3}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="location">Location</Label>
        <Input
          id="location"
          value={formData.location || ''}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
        />
      </div>
    </>
  );

  const renderAthleticFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="sport">Sport</Label>
        <Input
          id="sport"
          value={formData.athlete?.sport || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, sport: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="position">Position</Label>
        <Input
          id="position"
          value={formData.athlete?.position || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, position: e.target.value }
          })}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="height">Height</Label>
          <Input
            id="height"
            value={formData.athlete?.height || ''}
            onChange={(e) => setFormData({
              ...formData,
              athlete: { ...formData.athlete, height: e.target.value }
            })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="weight">Weight</Label>
          <Input
            id="weight"
            value={formData.athlete?.weight || ''}
            onChange={(e) => setFormData({
              ...formData,
              athlete: { ...formData.athlete, weight: e.target.value }
            })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="team">Team/Club</Label>
        <Input
          id="team"
          value={formData.athlete?.team_name || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, team_name: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="club_abbreviation">Club Abbreviation</Label>
        <Input
          id="club_abbreviation"
          value={formData.athlete?.club_abbreviation || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, club_abbreviation: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="competition_level">Competition Level</Label>
        <Select 
          value={formData.athlete?.competition_level || ''}
          onValueChange={(value) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, competition_level: value }
          })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="college">College</SelectItem>
            <SelectItem value="high_school">High School</SelectItem>
            <SelectItem value="amateur">Amateur</SelectItem>
            <SelectItem value="youth">Youth</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="nationality">Nationality</Label>
        <Input
          id="nationality"
          value={formData.athlete?.nationality || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, nationality: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="affiliate_organization">Affiliate Organization</Label>
        <Input
          id="affiliate_organization"
          value={formData.athlete?.affiliate_organization || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, affiliate_organization: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="affiliate_state">Affiliate State</Label>
        <Input
          id="affiliate_state"
          value={formData.athlete?.affiliate_state || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, affiliate_state: e.target.value }
          })}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="draft_round">Draft Round</Label>
          <Input
            id="draft_round"
            type="number"
            value={formData.athlete?.draft_round || ''}
            onChange={(e) => setFormData({
              ...formData,
              athlete: { ...formData.athlete, draft_round: e.target.value ? parseInt(e.target.value) : null }
            })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="draft_year">Year Drafted</Label>
          <Input
            id="draft_year"
            type="number"
            value={formData.athlete?.draft_year || ''}
            onChange={(e) => setFormData({
              ...formData,
              athlete: { ...formData.athlete, draft_year: e.target.value ? parseInt(e.target.value) : null }
            })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="college_university">College/University</Label>
        <Input
          id="college_university"
          value={formData.athlete?.college_university || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, college_university: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="coach">Coach</Label>
        <Input
          id="coach"
          value={formData.athlete?.coach_name || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, coach_name: e.target.value }
          })}
        />
      </div>
    </>
  );

  const renderPersonalFields = () => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">First Name</Label>
          <Input
            id="first_name"
            value={formData.athlete?.first_name || ''}
            onChange={(e) => setFormData({
              ...formData,
              athlete: { ...formData.athlete, first_name: e.target.value }
            })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">Last Name</Label>
          <Input
            id="last_name"
            value={formData.athlete?.last_name || ''}
            onChange={(e) => setFormData({
              ...formData,
              athlete: { ...formData.athlete, last_name: e.target.value }
            })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="date_of_birth">Date of Birth</Label>
        <Input
          id="date_of_birth"
          type="date"
          value={formData.athlete?.date_of_birth || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, date_of_birth: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="street_address">Street Address</Label>
        <Input
          id="street_address"
          value={formData.athlete?.street_address || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, street_address: e.target.value }
          })}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            value={formData.athlete?.city || ''}
            onChange={(e) => setFormData({
              ...formData,
              athlete: { ...formData.athlete, city: e.target.value }
            })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="state">State</Label>
          <Input
            id="state"
            value={formData.athlete?.state || ''}
            onChange={(e) => setFormData({
              ...formData,
              athlete: { ...formData.athlete, state: e.target.value }
            })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="zip_code">Zip Code</Label>
        <Input
          id="zip_code"
          value={formData.athlete?.zip_code || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, zip_code: e.target.value }
          })}
        />
      </div>
    </>
  );

  const renderContactFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="personal_email">Personal Email</Label>
        <Input
          id="personal_email"
          type="email"
          value={formData.athlete?.personal_email || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, personal_email: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="cell_phone">Cell Phone</Label>
        <Input
          id="cell_phone"
          type="tel"
          value={formData.athlete?.cell_phone || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, cell_phone: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="instagram_handle">Instagram Handle</Label>
        <Input
          id="instagram_handle"
          value={formData.athlete?.instagram_handle || ''}
          onChange={(e) => setFormData({
            ...formData,
            athlete: { ...formData.athlete, instagram_handle: e.target.value }
          })}
          placeholder="username (without @)"
        />
      </div>
    </>
  );

  const getSectionTitle = () => {
    switch (section) {
      case 'overview': return 'Edit Overview';
      case 'athletic': return 'Edit Athletic Details';
      case 'personal': return 'Edit Personal Information';
      case 'contact': return 'Edit Contact Info';
      default: return 'Edit Profile';
    }
  };

  const renderFields = () => {
    switch (section) {
      case 'overview': return renderOverviewFields();
      case 'athletic': return renderAthleticFields();
      case 'personal': return renderPersonalFields();
      case 'contact': return renderContactFields();
      default: return null;
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <div className="flex items-center justify-between">
            <DrawerTitle>{getSectionTitle()}</DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        <div className="px-4 pb-4 space-y-4">
          {renderFields()}
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} disabled={isLoading} className="flex-1">
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default AthleteProfileEditDrawer;